// Shopify API Service - works on both preview (via backend) and Netlify (via proxy to Shopify)
// Production backend URL - used when REACT_APP_BACKEND_URL is not set (e.g., on Netlify)
const PRODUCTION_BACKEND_URL = 'https://return-policy-update.preview.emergentagent.com';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || PRODUCTION_BACKEND_URL;
const SHOPIFY_CHECKOUT_URL = 'https://pinkeditnew.myshopify.com';

// Log which backend URL is being used (for debugging)
console.log('[ShopifyService] Using backend URL:', BACKEND_URL);

// Transform raw Shopify product JSON to the frontend format (mirrors backend transform_product)
function transformProduct(product, fromBackend = false) {
  const images = (product.images || []).map(img => ({
    url: img.src || '',
    altText: product.title || '',
    width: img.width,
    height: img.height
  }));

  const variants = [];
  const sizes = new Set();
  const colors = new Set();
  const options = product.options || [];

  (product.variants || []).forEach(v => {
    // Determine availability:
    // - If 'available' is explicitly true/false, use it
    // - If 'available' is null/undefined (public API doesn't provide it):
    //   - Check inventory_quantity if available
    //   - Otherwise default to true (can't determine from public API)
    let isAvailable;
    if (v.available === true) {
      isAvailable = true;
    } else if (v.available === false) {
      isAvailable = false;
    } else if (v.inventory_quantity !== null && v.inventory_quantity !== undefined) {
      isAvailable = v.inventory_quantity > 0;
    } else {
      // Public Shopify API doesn't provide availability info - default to true
      // The backend API (Storefront GraphQL) will provide accurate data
      isAvailable = true;
    }
    
    const variantData = {
      id: String(v.id || ''),
      title: v.title || '',
      availableForSale: isAvailable,
      quantityAvailable: v.inventory_quantity || 0,
      price: parseFloat(v.price || 0),
      currencyCode: 'USD',
      compareAtPrice: v.compare_at_price ? parseFloat(v.compare_at_price) : null,
      selectedOptions: [],
      image: null
    };

    options.forEach((opt, i) => {
      const optName = opt.name || '';
      const optValue = v[`option${i + 1}`];
      if (optValue) {
        variantData.selectedOptions.push({ name: optName, value: optValue });
        if (optName.toLowerCase() === 'size') sizes.add(optValue);
        if (optName.toLowerCase() === 'color') colors.add(optValue);
      }
    });

    if (v.featured_image) {
      variantData.image = {
        url: v.featured_image.src || '',
        altText: product.title || ''
      };
    }

    variants.push(variantData);
  });

  const firstVariant = (product.variants || [{}])[0];
  const price = parseFloat(firstVariant.price || 0);
  const compareAtPrice = firstVariant.compare_at_price ? parseFloat(firstVariant.compare_at_price) : null;

  let tags = product.tags || [];
  if (typeof tags === 'string') {
    tags = tags.split(',').map(t => t.trim()).filter(Boolean);
  }

  const isBestSeller = tags.some(t => ['best-seller', 'bestseller', 'best seller'].includes(t.toLowerCase()));
  const isNew = tags.some(t => ['new', 'new-arrival', 'new arrival'].includes(t.toLowerCase()));

  // Product is available if ANY variant is available
  const productAvailable = variants.length === 0 ? true : variants.some(v => v.availableForSale === true);

  return {
    id: String(product.id || ''),
    title: product.title || '',
    handle: product.handle || '',
    description: (product.body_html || '').replace(/<p>/g, '').replace(/<\/p>/g, '').replace(/<span>/g, '').replace(/<\/span>/g, ''),
    descriptionHtml: product.body_html || '',
    tags,
    productType: product.product_type || '',
    vendor: product.vendor || '',
    availableForSale: productAvailable,
    price,
    compareAtPrice,
    currencyCode: 'USD',
    images,
    featuredImage: images[0] || null,
    variants,
    sizes: sizes.size > 0 ? Array.from(sizes) : ['One Size'],
    colors: Array.from(colors),
    isBestSeller,
    isNew
  };
}

function transformCollection(collection) {
  const image = collection.image;
  return {
    id: String(collection.id || ''),
    title: collection.title || '',
    handle: collection.handle || '',
    description: collection.description || '',
    descriptionHtml: collection.description || '',
    image: {
      url: image ? (image.src || '') : '',
      altText: image ? (collection.title || '') : ''
    },
    products: [],
    productsCount: collection.products_count || 0
  };
}

// Fetch JSON with timeout and content-type check
async function tryFetch(url, timeoutMs = 8000) {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);
    if (!response.ok) return null;
    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('json')) return null;
    return await response.json();
  } catch (e) {
    return null;
  }
}

class ShopifyService {
  async getProducts(limit = 250, fresh = false) {
    // Try backend first (preview environment)
    if (BACKEND_URL) {
      const freshParam = fresh ? '&fresh=true' : '';
      const data = await tryFetch(`${BACKEND_URL}/api/shopify/products?limit=${limit}${freshParam}`);
      if (data?.products) return data;
    }

    // Fallback: Netlify proxy → Shopify (raw data needs transform)
    const raw = await tryFetch(`/shopify-api/products.json?limit=${limit}`);
    if (raw?.products) {
      return {
        products: raw.products.map(transformProduct),
        pageInfo: { hasNextPage: raw.products.length === limit },
        syncedAt: new Date().toISOString()
      };
    }

    throw new Error('Failed to fetch products');
  }

  async getProductByHandle(handle, fresh = false) {
    // Try backend first - this is the reliable source with accurate inventory
    if (BACKEND_URL) {
      const freshParam = fresh ? '?fresh=true' : '';
      const data = await tryFetch(`${BACKEND_URL}/api/shopify/products/${handle}${freshParam}`);
      if (data?.id) return data;
    }

    // Fallback: individual product endpoint (Shopify public JSON API)
    // Note: Public API doesn't include inventory data, so we use transformProduct
    // which will default unknown availability to true
    const raw = await tryFetch(`/shopify-api/products/${handle}.json`);
    if (raw?.product) {
      return transformProduct(raw.product);
    }

    throw new Error(`Product "${handle}" not found`);
  }

  async getCollections(limit = 50) {
    // Try backend first
    if (BACKEND_URL) {
      const data = await tryFetch(`${BACKEND_URL}/api/shopify/collections?limit=${limit}`);
      if (data?.collections) return data;
    }

    // Fallback
    const raw = await tryFetch(`/shopify-api/collections.json?limit=${limit}`);
    if (raw?.collections) {
      return { collections: raw.collections.map(transformCollection) };
    }

    throw new Error('Failed to fetch collections');
  }

  async getCollectionByHandle(handle) {
    // Try backend first
    if (BACKEND_URL) {
      const data = await tryFetch(`${BACKEND_URL}/api/shopify/collections/${handle}`);
      if (data?.id) return data;
    }

    // Fallback
    const raw = await tryFetch(`/shopify-api/collections/${handle}.json`);
    if (raw?.collection) {
      return transformCollection(raw.collection);
    }

    throw new Error(`Collection "${handle}" not found`);
  }

  // Build Shopify checkout URL from cart lines
  buildCheckoutUrl(lines) {
    if (!lines || lines.length === 0) return null;
    const cartItems = lines.map(l => `${l.merchandise.id}:${l.quantity}`).join(',');
    return `${SHOPIFY_CHECKOUT_URL}/cart/${cartItems}`;
  }
  
  // Refresh backend cache to get fresh inventory data
  async refreshCache() {
    if (BACKEND_URL) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/shopify/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          console.log('Backend cache refreshed');
          return true;
        }
      } catch (e) {
        console.error('Error refreshing cache:', e);
      }
    }
    return false;
  }
}

export const shopifyService = new ShopifyService();
export default shopifyService;
